import '../constants/const.css';
import './css/pageDraftConditionSetting.css';

import React, { useState, useEffect, useMemo, useRef } from 'react';

import 'bootstrap/dist/css/bootstrap.min.css';
import Button from 'react-bootstrap/Button';

import {
  DraftConditionGetResponseIndexMap,
  DraftConditionFormIndexMap as JsonMap,
  DraftConditionFormIndexMap,
  DraftConditionFormValuesObject,
  DraftConditionGetResponseObject,
  FurnacesStatus,
  divideCarryVolumeIntoMonthly,
} from '../entities/draftCondition';

import { useApi, REQ_DRAFT_CONDITION, JsonResponse } from '../logic/classApi';
import { MESSAGES, PERMISSIONS, PATH_DRAFT_CONDITION_SETTING } from '../constants/const';
import { openNewTabComponent } from '../App';
import AppPane, { showConfirmMessage, showErrorMessage, showMessage, ShownError, showWarningMessage } from '../components/appPane';
import { ceilNumber, toISODateStr, toNumberProps } from '../logic/util';
import { LoginService } from '../logic/loginService';

import PastResult from '../pages/pagePastResult';
import NumberInput from '../components/numberInput';
import FormInput from '../components/formInput';

import 'react-datepicker/dist/react-datepicker.css';
import { ja } from 'date-fns/locale';
import { CustomDatePicker } from '../components/customDatePicker';

const Page = () => {
  const [isInitialized, setInitialized] = useState<boolean>(false);
  const [getResponse, setGetResponse] = useState<DraftConditionGetResponseObject>(new DraftConditionGetResponseObject());

  const formValueState = useState<DraftConditionFormValuesObject>(new DraftConditionFormValuesObject());
  const [formValues, setFormValues] = formValueState;

  const [apiRequestWatcher, setApiRequestWatcher] = useState<number>(-1);

  const initialMonthlyCarryCalculationRef = useRef(new Array(12).fill(0));
  const initialWeeklyCarryCalculationRef = useRef(new Array(7).fill(0));

  const { branchId, userId, furnacesCount, permissions } = LoginService.getLoginInfo();
  const isEditablePermission = Boolean(permissions & (PERMISSIONS.DRAFT_CONDITION_MAIN_EDIT | PERMISSIONS.DRAFT_CONDITION_SUB_EDIT | PERMISSIONS.DRAFT_CONDITION_OTHER_EDIT));

  const { getRequest, postRequestJson } = useApi();

  const newWindowRef = useRef<Window | null>(null);

  const ReferenceOfPlanCarryVolume = {
    result: 1 << 0,
    plan: 1 << 1,
    cycle: 1 << 2,
  };

  useEffect(() => {
    return () => {
      if (newWindowRef.current && !newWindowRef.current.closed) {
        newWindowRef.current.close();
      }
    };
  }, []);

  const [current, setCurrent] = useState<Date>(new Date());
  const currentYear = current.getFullYear();
  const currentMonthIndex = current.getMonth();
  const thisFiscalYear = currentMonthIndex + 1 < 4 ? currentYear - 1 : currentYear;
  const thisFiscalYearStart = new Date(thisFiscalYear, 3, 1, 9);
  const thisFiscalYearEnd = new Date(thisFiscalYear + 1, 2, 31, 9);
  const nextFiscalYear = thisFiscalYear + 1;
  const nextFiscalYearStart = useMemo(() => new Date(thisFiscalYear + 1, 3, 1, 9), [thisFiscalYear]);
  const nextFiscalYearEnd = useMemo(() => new Date(thisFiscalYear + 2, 2, 31, 9), [thisFiscalYear]);
  const selectYear = new Date(formValues.draftConditionStart).getFullYear();
  const selectMonthIndex = new Date(formValues.draftConditionStart).getMonth();
  const selectFiscalYear = selectMonthIndex + 1 < 4 ? selectYear - 1 : selectYear;

  const yearlyTrashCarryVolume = formValues.planCarryVolume.reduce((total, volume) => total + Number(volume), 0);

  /** 年間焼却ごみ搬入量から計画搬入量をセット */
  const updateYearlyTrashByPlan = () => {
    setFormValues({ ...formValues, yearlyTrashCarryVolume });
    setTimeout(updateCsvRecords, 1000);
  };

  const getWeekContext = () => {
    // 立案開始年度の始めから計算を開始する
    const startDate = new Date(formValues.draftConditionStart);
    if (startDate.getMonth() < 3) {
      startDate.setFullYear(startDate.getFullYear() - 1);
    }
    startDate.setMonth(3);
    startDate.setDate(1);
    const endDate = new Date(formValues.draftConditionEnd);

    const weekMap = new Array(12).fill(null);
    for (let target = new Date(startDate); target <= endDate; target.setDate(target.getDate() + 1)) {
      if (!weekMap[target.getMonth()]) {
        weekMap[target.getMonth()] = new Array(7).fill(0);
      }
      const dayOfWeek = getDayOfWeek(target);
      weekMap[target.getMonth()][dayOfWeek]++; // 同月内の各曜日の数をカウント
    }

    return { weekMap, startDate, endDate };
  };

  /** 月曜を0とする曜日インデックスを取得 月曜始まりの配列weeklyRate用 */
  const getDayOfWeek = (date: Date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  };

  /** 計画搬入量からCSVレコードを更新 */
  const updateCsvRecords = (planCarryVolume = formValues.planCarryVolume) => {
    const { startDate, weekMap } = getWeekContext();
    let planCarryVolumeCsvRecords = [] as [string, number][];

    for (let i = 0; i < 12; i++) {
      // 4月始まりでループ
      const month = ((i + 3) % 12) + 1;
      const monthIndex = month - 1;
      const weekDays = weekMap[monthIndex];
      const targetYear = monthIndex >= startDate.getMonth() ? startDate.getFullYear() : startDate.getFullYear() + 1;

      for (let targetDate = new Date(targetYear, monthIndex, 1, 9); targetDate.getMonth() === monthIndex; targetDate.setDate(targetDate.getDate() + 1)) {
        const targetDayOfWeek = getDayOfWeek(targetDate);
        const weeklyRate = formValues.weeklyCarryCalculation[targetDayOfWeek] / 100;
        const targetDayVolume = !weekDays || weekDays[targetDayOfWeek] === 0 ? 0 : (Number(planCarryVolume[i]) * weeklyRate) / weekDays[targetDayOfWeek];

        planCarryVolumeCsvRecords.push([toISODateStr(targetDate) || '', targetDayVolume]);
      }
    }

    formValues.planCarryVolumeCsvRecords = planCarryVolumeCsvRecords;
    setFormValues((prev) => ({
      ...prev, // クロージャ回避(計画搬入量を月を跨いで素早く変更された場合に更新が遅れるのを回避する)
      planCarryVolumeCsvRecords,
      yearlyTrashCarryVolume,
    }));
    return planCarryVolumeCsvRecords;
  };

  /** 年間焼却ごみ搬入量から計画搬入量をセット */
  const updatePlanByYearlyTrash = () => {
    const { startDate, weekMap } = getWeekContext();

    const planCarryVolume = new Array(12).fill(0);

    if (selectFiscalYear == thisFiscalYear) {
      // 今年度の計画搬入量を計算
      for (let i = 0; i < 12; i++) {
        if (i < currentMonthIndex - 3) {
          // 計画値ではなく実績値をそのままセット
          planCarryVolume[i] += formValues.planCarryVolume[i];
        } else {
          // 実績値を除外した状態で計画値を算出してセット
          const month = ((i + 3) % 12) + 1;
          const monthIndex = month - 1;
          const weekDays = weekMap[monthIndex];
          const targetYear = monthIndex >= startDate.getMonth() ? startDate.getFullYear() : startDate.getFullYear() + 1;

          for (let targetDate = new Date(targetYear, monthIndex, 1, 9); targetDate.getMonth() === monthIndex; targetDate.setDate(targetDate.getDate() + 1)) {
            const monthlyRate =
              formValues.monthlyCarryCalculation[i] / formValues.monthlyCarryCalculation.slice(currentMonthIndex - 3).reduce((total, volume) => total + volume, 0);
            const targetDayOfWeek = getDayOfWeek(targetDate);
            const weeklyRate = formValues.weeklyCarryCalculation[targetDayOfWeek] / 100;
            const targetDayVolume =
              !weekDays || weekDays[targetDayOfWeek] === 0
                ? 0
                : ((formValues.yearlyTrashCarryVolume - yearlyTrashCarryVolumeMin) * monthlyRate * weeklyRate) / weekDays[targetDayOfWeek];

            planCarryVolume[i] += targetDayVolume;
          }
        }
      }
    } else {
      // 来年度の計画搬入量を計算
      for (let i = 0; i < 12; i++) {
        const month = ((i + 3) % 12) + 1;
        const monthIndex = month - 1;
        const weekDays = weekMap[monthIndex];
        const targetYear = monthIndex >= startDate.getMonth() ? startDate.getFullYear() : startDate.getFullYear() + 1;

        for (let targetDate = new Date(targetYear, monthIndex, 1, 9); targetDate.getMonth() === monthIndex; targetDate.setDate(targetDate.getDate() + 1)) {
          const monthlyRate = formValues.monthlyCarryCalculation[i] / 100;
          const targetDayOfWeek = getDayOfWeek(targetDate);
          const weeklyRate = formValues.weeklyCarryCalculation[targetDayOfWeek] / 100;
          const targetDayVolume = !weekDays || weekDays[targetDayOfWeek] === 0 ? 0 : (formValues.yearlyTrashCarryVolume * monthlyRate * weeklyRate) / weekDays[targetDayOfWeek];

          planCarryVolume[i] += targetDayVolume;
        }
      }
    }

    const planCarryVolumeCsvRecords = updateCsvRecords(planCarryVolume);

    setFormValues({
      ...formValues,
      planCarryVolumeCsvRecords,
      planCarryVolume,
    });
  };

  const [draftConditionStart, setDraftConditionStart] = useState<string>('');
  const draftConditionEnd = useMemo(() => {
    const value = formValues.draftConditionStart ? toISODateStr(new Date(formValues.draftConditionStart) <= thisFiscalYearEnd ? thisFiscalYearEnd : nextFiscalYearEnd) || '' : '';
    return value;
  }, [formValues.draftConditionStart]);

  const draftConditionStartBefore = useMemo(() => {
    if (!draftConditionStart) {
      return '';
    }
    const date = new Date(draftConditionStart);
    date.setDate(date.getDate() - 1);
    return toISODateStr(date);
  }, [draftConditionStart]);

  const maxDraftConditionStart = useMemo(() => {
    const end = new Date(nextFiscalYearEnd);
    end.setMonth(end.getMonth() - 3);
    return toISODateStr(end);
  }, [formValues.draftConditionEnd, nextFiscalYearEnd]);

  const draftConditionStartYear = new Date(draftConditionStart).getFullYear();
  const draftConditionStartMonthIndex = new Date(draftConditionStart).getMonth();
  const draftConditionFiscalYearStart = draftConditionStartMonthIndex + 1 < 4 ? draftConditionStartYear - 1 : draftConditionStartYear;

  const yearlyTrashCarryVolumeMin = 0;

  const isFileMode = useMemo(() => formValues.settingMode === 'file', [formValues.settingMode]);
  const isCalculationMode = useMemo(() => formValues.settingMode === 'calculation', [formValues.settingMode]);

  const isInputAnyPlanCarryVolume: boolean = useMemo(() => formValues.planCarryVolume.some((value) => !!value), [formValues.planCarryVolume]);

  const isChangedSavedCommonShutdownPlan = useMemo(() => {
    const savedCommonPlans = getResponse.savedDraftCondition?.shutdownDatePlans?.concat().shift();
    const [_key, _furnaces, shutdownDate, launchDate, shutdownDateFLG, launchDateFLG] = formValues.commonShutdownDatePlan;

    console.log([savedCommonPlans, { shutdownDate, launchDate }]);
    // 優先度は不変のため比較しない
    return (
      savedCommonPlans &&
      ((savedCommonPlans.shutdownDate && savedCommonPlans.shutdownDate !== shutdownDate) || (savedCommonPlans.launchDate && savedCommonPlans.launchDate !== launchDate))
    );
  }, [formValues.commonShutdownDatePlan]);

  useEffect(() => {
    LoginService.setScreenInfo(PATH_DRAFT_CONDITION_SETTING);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const reqUrl = `${REQ_DRAFT_CONDITION}?branchId=${branchId}&draftConditionStart=${draftConditionStart}`;

        const { payload: json } = await getRequest<JsonResponse>(reqUrl);
        console.log('json', json);

        const filteredResponse = Object.keys(DraftConditionGetResponseIndexMap).reduce((obj: any, key) => {
          if (!(typeof json[key] === 'undefined')) {
            obj[key] = json[key];
          }
          return obj;
        }, {});

        const mergedResponse = {
          ...new DraftConditionGetResponseObject(),
          ...filteredResponse,
        };

        setGetResponse(mergedResponse);
        setCurrent(new Date(mergedResponse.current));

        const formValuesByJson = Object.keys(DraftConditionFormIndexMap).reduce((obj: any, key) => {
          if (!(typeof json[key] === 'undefined')) {
            obj[key] = json[key];
          }
          return obj;
        }, {});

        const newFormValues = {
          // 初期化
          ...new DraftConditionFormValuesObject(furnacesCount),
          // フォームの入力値オブジェクトと同一プロパティ名の値の詰替え
          ...formValuesByJson,
          // 一時保存データがあればその値を優先
          ...(mergedResponse.savedDraftCondition
            ? DraftConditionFormValuesObject.restoreTempSavedCondition({
                tempSavedCondition: mergedResponse.savedDraftCondition,
                responseFurnacesCount: mergedResponse.furnacesCount,
                furnacesCount,
                isInitialized,
              })
            : {}),

          // 立案開始期間は一時保存の値ではなく別途含まれているGetAPIレスポンスの方を優先
          draftConditionStart: formValuesByJson.draftConditionStart,
          draftConditionEnd: formValuesByJson.draftConditionEnd,
        };

        newFormValues.settingMode = newFormValues.settingCsvPath ? 'file' : 'calculation';

        // 炉状態は実績・計画値を最優先で上書き
        (mergedResponse.status as FurnacesStatus[]).forEach(({ isShutdown, days, date }, i) => {
          if (date) {
            newFormValues.untilFurnacesState[i] = [isShutdown ? 0 : 1, days];
          }
        });

        initialMonthlyCarryCalculationRef.current = [...newFormValues.monthlyCarryCalculation];
        initialWeeklyCarryCalculationRef.current = [...newFormValues.weeklyCarryCalculation];

        console.log('newFormValues', newFormValues);

        setFormValues(newFormValues);

        setDraftConditionStart(newFormValues.draftConditionStart);
      } catch (e) {
        console.error(e);
      }
    })();
    setInitialized(true);
  }, [apiRequestWatcher]);

  const changeDraftDate = (e?: React.MouseEvent<HTMLButtonElement>) => {
    // APIリクエスト・再描画トリガー
    setDraftConditionStart(formValues.draftConditionStart);

    console.log({
      draftConditionStart: formValues.draftConditionStart,
      draftConditionEnd: formValues.draftConditionEnd,
    });

    setApiRequestWatcher(Date.now());
  };

  const validateForm = () => {
    const messages = [];

    if (isFileMode && (!formValues.planCarryVolumeCsvRecords.length || !formValues.settingCsvPath)) {
      messages.push('ごみ搬入量設定ファイルが指定されていません。');
    }

    if (hasErrorTotalMonthly) {
      messages.push('月別搬入割合は合計が100%になるように入力してください。');
    }

    if (hasErrorTotalWeekly) {
      messages.push('曜日別搬入割合は合計が100%になるように入力してください。');
    }

    if (isCalculationMode && !isInputAnyPlanCarryVolume) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('月別計画搬入量'));
    }

    // if (formValues.maxContinuousOperation.some((count) => !count)) {
    //   messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('連続運転最大日数'));
    // }

    // if (formValues.shutdowns.some((shutdown) => !shutdown)) {
    //   messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('停炉回数'));
    // }

    if (!formValues.commonShutdownDatePlan[2]) {
      messages.push('共通停炉期間の立下げ日を入力してください');
    }
    if (!formValues.commonShutdownDatePlan[3]) {
      messages.push('共通停炉期間の立上げ日を入力してください');
    }

    if (messages.length) {
      showErrorMessage(messages);
      return false;
    }

    return true;
  };

  /** 立案条件の保存や自動立案ツールの実行をリクエスト */
  const handleRegister = async (sendAutoDraftTool = false) => {
    const isValidForm = sendAutoDraftTool ? validateForm() : true;
    if (!isValidForm) {
      return;
    }

    if (!(await showConfirmMessage(sendAutoDraftTool ? '立案を開始します。よろしいですか。' : MESSAGES.CONFIRM_SAVE_FORM))) {
      return;
    }

    if (isChangedSavedCommonShutdownPlan && !(await showWarningMessage(`すでに共通停止期間が登録済みです。続行して設定を変更しますか。`))) {
      return;
    }

    if (
      !(draftConditionStart === formValues.draftConditionStart && draftConditionEnd === formValues.draftConditionEnd) &&
      !(await showWarningMessage('立案期間の切替がされていません。続行しますか。'))
    ) {
      return;
    }

    if (isCalculationMode) {
      const planCarryVolumeCsvRecords = updateCsvRecords(formValues.planCarryVolume);
      formValues.planCarryVolumeCsvRecords = planCarryVolumeCsvRecords;
    }

    // CSVの日付は立案開始期間の年と見なす
    const startDateYear = new Date(formValues.draftConditionStart).getFullYear();
    const endDateYear = new Date(formValues.draftConditionEnd).getFullYear();

    const csvStartDateYear = new Date((formValues.planCarryVolumeCsvRecords.concat().shift() || [startDateYear])[0]).getFullYear();
    const csvEndDateYear = new Date((formValues.planCarryVolumeCsvRecords.concat().pop() || [endDateYear])[0]).getFullYear();

    formValues.planCarryVolumeCsvRecords = formValues.planCarryVolumeCsvRecords.map(([date, value]) => {
      // 日付年数の置換
      const rep1 = date.replace(`${csvStartDateYear}`, `${startDateYear}`);
      const rep2 = date.replace(`${csvEndDateYear}`, `${endDateYear}`);

      return [rep1 === date ? (rep2 === date ? date : rep2) : rep1, value];
    });

    if (isFileMode) {
      formValues.planCarryVolume = divideCarryVolumeIntoMonthly(formValues.planCarryVolumeCsvRecords, formValues.draftConditionStart);
      formValues.yearlyTrashCarryVolume = formValues.planCarryVolume.reduce((sum, item) => sum + item, 0);
    }

    const params = {
      ...formValues,
      draftConditionEnd,
      yearlyTrashCarryVolume: formValues.yearlyTrashCarryVolume,
      branchId,
      userId,
      sendAutoDraftTool,
      settingCsvPath: isFileMode ? formValues.settingCsvPath : '',
      planCarryVolume: formValues.planCarryVolume,
      planCarryVolumeCsvRecords: formValues.planCarryVolumeCsvRecords,
      shutdownDatePlans: [
        {
          furnaces: 0,
          shutdownDate: formValues.commonShutdownDatePlan[2] ?? '',
          launchDate: formValues.commonShutdownDatePlan[3] ?? '',
          shutdownDateFlag: formValues.commonShutdownDatePlan[4],
          launchDateFlag: formValues.commonShutdownDatePlan[5],
          notChangeFlag: true,
        },
        /* 
        {
          個別停炉期間の一時コメントアウトに合わせて当該箇所もコメントアウトに変更
          ...formValues.shutdownDatePlans
          .filter(([_id, furnaces, shutdownDate, launchDate]) => furnaces && shutdownDate && launchDate)
          .map(([_id, furnaces, shutdownDate, launchDate, priorityFurnacesShutdown]) => ({
            furnaces,
            shutdownDate,
            launchDate,
            priorityFurnacesShutdown,
            notChangeFlag: false,
          })),
        },
        */
      ],
    };
    console.log('sendAutoDraftTool', sendAutoDraftTool);
    toNumberProps(params);

    let draftConditionResponseMessage;
    await showMessage({
      message: sendAutoDraftTool ? MESSAGES.SUCCESS_AUTO_DRAFT_REQUEST : MESSAGES.SUCCESS_SAVE_FORM,
      type: 'success',
      showOnResolved: async () => {
        const { payload } = await postRequestJson<JsonResponse>(REQ_DRAFT_CONDITION, params);
        console.log(payload);
        const { messages } = payload;
        if (messages.length) {
          const message = messages.pop();
          draftConditionResponseMessage = {
            type: message.level,
            message: message.text,
          };
        }
      },
    });

    if (draftConditionResponseMessage) {
      showMessage(draftConditionResponseMessage);
    }
  };

  const inputByFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget.previousElementSibling as HTMLInputElement)?.click();
  };

  const clearFileInputValue = () => {
    (document.querySelector<HTMLInputElement>('#importFile') as HTMLInputElement).value = '';
  };
  const deleteInputByFile = () => {
    setFormValues({
      ...formValues,
      settingCsvPath: '',
      planCarryVolumeCsvRecords: [],
    });
    clearFileInputValue();
  };

  const applyByFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget?.files && e.currentTarget?.files.length ? e.currentTarget.files[0] : undefined;

    if (!file) {
      clearFileInputValue();
      return;
    }

    (async () => {
      if (!/.*ごみ搬入量.*.csv$/.test(file.name)) {
        const isConfirmed = await showWarningMessage('指定されたファイルはごみ搬入量のCSVファイルではない可能性があります。続行しますか。');
        if (!isConfirmed) {
          clearFileInputValue();
          return;
        }
      }

      const text = await file.text();
      const records = text
        .trim()
        .split(/\r?\n/)
        .map((record) =>
          record
            .replace(/,+$/, '')
            .split(',')
            .map((value) => value.trim()),
        );

      // ヘッダがあれば読み飛ばし (最後のカラムの値が数値ではない)
      if (records.length && !/^[0-9.]+$/.test(records[0].concat().pop() || '')) {
        records.shift();
      }

      if (!records.length) {
        throw new ShownError('読み込んだCSVファイルにレコードがありません');
      }

      setFormValues({
        ...formValues,
        settingCsvPath: file.name,
        planCarryVolumeCsvRecords: records.map(([date, volume]) => {
          try {
            let isoDateStr = '';
            isoDateStr = toISODateStr(date);
            if (!isoDateStr) {
              throw new Error('ISO形式への変換に失敗しました');
            }
            return [isoDateStr || '', Number(volume)];
          } catch (e: unknown) {
            let prevError = e instanceof Error ? e : undefined;
            throw new ShownError('読み込んだCSVファイルの日付データのISO形式への変換に失敗しました', prevError);
          }
        }),
      });
    })().catch((err) => {
      console.error(err);
      // 既存のファイルはそのままでfile入力要素の値だけクリア
      clearFileInputValue();

      showErrorMessage((err instanceof ShownError ? err.message : '') || MESSAGES.ERROR_IMPORT_FILE);
    });
  };

  // // 一時コメントアウト 個別炉停止期間は一時非表示
  // const addShutdownDatePlanField = (e?: React.MouseEvent<HTMLButtonElement>) => {
  //   console.log('addShutdownDatePlanField', formValues);
  //   formValues.shutdownDatePlans.push([Date.now(), 1, '', '', Priority.low]);
  //   setFormValues({ ...formValues });
  // };

  // // 一時コメントアウト 個別炉停止期間は一時非表示
  // const deleteShutdownDatePlanField = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
  //   const newPlans = formValues.shutdownDatePlans.filter((plan, i) => i !== index);

  //   // 空になったら追加しなおす
  //   if (!newPlans.length) {
  //     newPlans.push([Date.now(), 1, '', '', Priority.low]);
  //   }

  //   setFormValues({
  //     ...formValues,
  //     shutdownDatePlans: newPlans,
  //   });
  // };

  // 焼却負荷率から処理量の逆算(N炉動かした際の焼却負荷率/100 * 定格処理量 * 動かす炉数N)
  const operationalProcess: number[] = useMemo(
    () => formValues.burnLoad.map((eachBurnLoad, i) => (eachBurnLoad / 100) * getResponse.rateCapacity * (i + 1)),
    [formValues.burnLoad, getResponse.rateCapacity],
  );

  const totalMonthly: number = useMemo(
    () => formValues.monthlyCarryCalculation.reduce((total, rate) => total + Number((rate * 100).toFixed()), 0) / 100,
    [formValues.monthlyCarryCalculation],
  );
  const totalWeekly: number = useMemo(
    () => formValues.weeklyCarryCalculation.reduce((total, rate) => total + Number((rate * 100).toFixed()), 0) / 100,
    [formValues.weeklyCarryCalculation],
  );

  const hasErrorTotalMonthly: boolean = useMemo(() => totalMonthly !== 100, [totalMonthly]);
  const hasErrorTotalWeekly: boolean = useMemo(() => totalWeekly !== 100, [totalWeekly]);

  const isInputAnyMonthly: boolean = useMemo(() => totalMonthly > 0, [totalMonthly]);
  const isInputAnyWeekly: boolean = useMemo(() => totalWeekly > 0, [totalWeekly]);

  /**
   * 搬入割合をリセットする
   */
  const resetCarryCalculation = async () => {
    const confirm = await showConfirmMessage(MESSAGES.CONFIRM_RESET_CARRY_CALCULATION);
    if (confirm) {
      formValues.monthlyCarryCalculation = [...initialMonthlyCarryCalculationRef.current];
      formValues.weeklyCarryCalculation = [...initialWeeklyCarryCalculationRef.current];
      updatePlanByYearlyTrash();
    }
  };

  const getBackgroundColorByReferenceOfCarryVolume = (index: number): string => {
    if (index < 0 && index >= 12) return '';

    if (formValues.referenceOfCarryVolume[index] === ReferenceOfPlanCarryVolume.cycle) {
      /* NOTE:元々「一時保存」('')にしていたが、分かりづらいと意見が出たため、再帰情報の場合も「計画」(var(--color-triad-2))に変更
       * 元に戻す可能性があるため、処理は流用する形で修正
       */
      return 'var(--color-triad-2)';
    } else if (formValues.referenceOfCarryVolume[index] === ReferenceOfPlanCarryVolume.result) {
      return 'var(--color-triad-1)';
    } else if (formValues.referenceOfCarryVolume[index] === ReferenceOfPlanCarryVolume.plan) {
      return 'var(--color-triad-2)';
    } else if (formValues.referenceOfCarryVolume[index] === (ReferenceOfPlanCarryVolume.result | ReferenceOfPlanCarryVolume.plan)) {
      return 'var(--color-plan-2)';
    } else {
      return '';
    }
  };

  const getTextByReferenceOfCarryVolume = (index: number): string => {
    if (index < 0 && index >= 12) return '';
    if (formValues.referenceOfCarryVolume[index] === ReferenceOfPlanCarryVolume.cycle) {
      /* NOTE:元々「一時保存」にしていたが、分かりづらいと意見が出たため、再帰情報の場合も「計画」に変更
       * 元に戻す可能性があるため、処理は流用する形で修正
       */
      return '計画';
    } else if (formValues.referenceOfCarryVolume[index] === ReferenceOfPlanCarryVolume.result) {
      return '実績';
    } else if (formValues.referenceOfCarryVolume[index] === ReferenceOfPlanCarryVolume.plan) {
      return '計画';
    } else if (formValues.referenceOfCarryVolume[index] === (ReferenceOfPlanCarryVolume.result | ReferenceOfPlanCarryVolume.plan)) {
      return '実績/計画';
    } else {
      return '';
    }
  };

  /**
   * 基本設定を初期設定に戻す
   * 将来的に初期設定に戻す処理は必要だが、仕様が詰めれていないため、未使用
   */
  const handleResetToOriginForBasicButtonClick = async () => {
    const confirm = await showConfirmMessage(MESSAGES.CONFIRM_RESET_TO_ORIGIN);
    if (confirm) {
      const formValuesByGetResponse: Partial<DraftConditionFormValuesObject> = {
        pitLimit: getResponse.pitLimit,
        pitMax: getResponse.pitMax,
        pitMin: getResponse.pitMin,
        trashDensity: getResponse.trashDensity,
        ashRate: getResponse.ashRate,
        truckMainAshCarryOut: getResponse.truckMainAshCarryOut,
        flyAshRate: getResponse.flyAshRate,
        burnLoad: getResponse.burnLoad,
        truckFlyAshCarryOut: getResponse.truckFlyAshCarryOut,
      };
      console.log(formValuesByGetResponse);
      setFormValues((prev) => ({
        ...prev,
        ...formValuesByGetResponse,
      }));
    }
  };

  /**
   * 搬入割合を初期設定に戻す
   * 将来的に初期設定に戻す処理は必要だが、仕様が詰めれていないため、未使用
   */
  const handleResetToOriginForCarryCalculationButtonClick = async () => {
    const confirm = await showConfirmMessage(MESSAGES.CONFIRM_RESET_TO_ORIGIN);
    if (confirm) {
      const formValuesByGetResponse: Partial<DraftConditionFormValuesObject> = {
        monthlyCarryCalculation: getResponse.monthlyCarryCalculation,
        weeklyCarryCalculation: getResponse.weeklyCarryCalculation,
      };
      console.log(formValuesByGetResponse);
      setFormValues((prev) => ({
        ...prev,
        ...formValuesByGetResponse,
      }));
    }
  };

  const DATE_FORMAT_YYYYMMDD = 'yyyy-MM-dd';
  const DATE_FORMAT_YYYYMMDD_SLASH = 'yyyy/MM/dd';
  const WEEK_DAYS = ['日', '月', '火', '水', '木', '金', '土'];
  return (
    <div className="screen-draftsetting">
      <div className="back-draftsetting"></div>

      <br></br>

      <div className="header-draftsetting"></div>
      <div className="item-title-draftsetting">
        基本設定
        {/* 将来的に初期設定に戻す処理は必要だが、仕様が詰めれていないため、一時コメントアウト */}
        {/* <Button variant="outline-light" size="sm" className="ml-1" onClick={handleResetToOriginForBasicButtonClick}>
          初期設定に戻す
        </Button> */}
      </div>
      <div className="indent-draftsetting tablet-draftsetting mt-1">
        <div className="row-draftsetting">
          <div className="required-draftsetting field-name-draftsetting">
            <div className="fieldname-back-draftsetting"></div>
            立案期間
          </div>
          <div className="field-values-draftsetting">
            <div className="field-value-draftsetting">
              <div>
                <CustomDatePicker
                  className="dateinput-draftsetting"
                  minDate={current}
                  maxDate={new Date(nextFiscalYearStart)}
                  selected={formValues.draftConditionStart ? new Date(formValues.draftConditionStart) : current}
                  onChange={(date: Date | null) => {
                    if (date != null) {
                      setFormValues({
                        ...formValues,
                        draftConditionStart: toISODateStr(date),
                        draftConditionEnd: new Date(date) > thisFiscalYearEnd ? toISODateStr(nextFiscalYearEnd) : toISODateStr(thisFiscalYearEnd),
                      });
                    }
                  }}
                  dateFormat={`${DATE_FORMAT_YYYYMMDD_SLASH}(${WEEK_DAYS[formValues.draftConditionStart ? new Date(formValues.draftConditionStart).getDay() : current.getDay()]})`}
                  portalId="datepicker-portal"
                  locale={ja}
                  disabled={!isEditablePermission}
                />
              </div>
              <div className="mx-1">～</div>
              <div>
                <CustomDatePicker
                  className="dateinput-draftsetting"
                  minDate={current}
                  maxDate={new Date(maxDraftConditionStart)}
                  selected={formValues.draftConditionEnd ? new Date(formValues.draftConditionEnd) : thisFiscalYearEnd}
                  onChange={(date: Date | null) => {}}
                  dateFormat={`${DATE_FORMAT_YYYYMMDD_SLASH}(${
                    WEEK_DAYS[formValues.draftConditionEnd ? new Date(formValues.draftConditionEnd).getDay() : thisFiscalYearEnd.getDay()]
                  })`}
                  portalId="datepicker-portal"
                  locale={ja}
                  readOnly={true}
                  disabled={!isEditablePermission}
                />
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                className="inputbutton-draftsetting"
                disabled={!isEditablePermission || (draftConditionStart === formValues.draftConditionStart && draftConditionEnd === formValues.draftConditionEnd)}
                onClick={changeDraftDate}
              >
                立案期間切替
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="indent-draftsetting tablet-draftsetting d-flex">
        <div className="row-draftsetting-half">
          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting"></div>
              トラブル時ピット限界値
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="2"
                  min={0}
                  data-key={[JsonMap.pitLimit]}
                  value={formValues.pitLimit}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting">m3</span>
              </div>
              <div className="field-value-draftsetting">
                <div>：{ceilNumber(formValues.pitLimit * formValues.trashDensity, 2).toLocaleString()}</div>
                <div className="unit-draftsetting">t</div>
              </div>
            </div>
          </div>
          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting"></div>
              適正ピット残最大値
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="2"
                  min={0}
                  data-key={[JsonMap.pitMax]}
                  value={formValues.pitMax}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting">m3</span>
              </div>
              <div className="field-value-draftsetting">
                <div>：{ceilNumber(formValues.pitMax * formValues.trashDensity, 2).toLocaleString()}</div>
                <div className="unit-draftsetting">t</div>
              </div>
            </div>
          </div>
          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting"></div>
              適正ピット残最小値
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="2"
                  min={0}
                  data-key={[JsonMap.pitMin]}
                  value={formValues.pitMin}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting">m3</span>
              </div>
              <div className="field-value-draftsetting">
                <div>：{ceilNumber(formValues.pitMin * formValues.trashDensity, 2).toLocaleString()}</div>
                <div className="unit-draftsetting">t</div>
              </div>
            </div>
          </div>
          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting" style={{ height: '100%' }}></div>
              {draftConditionStartBefore}の状態
            </div>
            <div className="field-values-draftsetting">
              <div>
                {(getResponse.status || []).map((status, i) => {
                  return (
                    <div key={`status_${i}`}>
                      <div className="field-value-draftsetting my-2">
                        <span>{i + 1}号炉</span>
                        <span className="mx-2">・・・</span>
                        <FormInput formValueState={formValueState} data-key={[JsonMap.untilFurnacesState, i, 0]}>
                          <select className="d-select mx-1" disabled={!isEditablePermission || !!status.date} value={formValues.untilFurnacesState[i][0]}>
                            <option value="1">稼働</option>
                            <option value="0">停炉</option>
                          </select>
                        </FormInput>

                        <NumberInput
                          formValueState={formValueState}
                          disabled={!isEditablePermission || !!status.date}
                          className="input-draftsetting ml-1"
                          min={0}
                          data-number-type="integer"
                          data-key={[JsonMap.untilFurnacesState, i, 1]}
                          value={formValues.untilFurnacesState[i][1]}
                        />
                        <span className="unit-draftsetting">日</span>
                      </div>
                      <div className="field-value-draftsetting my-2">
                        {status.date ? (
                          <span>
                            <span className="d-inline-block mx-2">{status.isShutdown ? '停炉' : '稼働'}日</span>
                            <span>{status.date}</span>
                            <span className="mx-1">～</span>
                            <span>{draftConditionStartBefore}</span>
                            <span className="mx-1">まで</span>
                            <span>{status.days}日</span>
                          </span>
                        ) : (
                          <span></span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting" style={{ height: '50px' }}></div>
              {draftConditionStartBefore}の<br></br>ごみピット残量
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="2"
                  min={0}
                  data-key={[JsonMap.untilTrashPitRest]}
                  value={formValues.untilTrashPitRest}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting">t</span>
                {getResponse.trashPitRestContext.date && (
                  <>
                    <span>{getResponse.trashPitRestContext.reference === 'plan' ? '計画' : '実績'}：</span>
                    <span>{Number(ceilNumber(getResponse.trashPitRestContext.trashPitRest ?? 0, 2)).toLocaleString()}</span>
                    <span className="unit-draftsetting">t</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="row-draftsetting-half">
          {operationalProcess.map((_v, i) => {
            return (
              <div key={`burnVolume_${i}`} className="row-draftsetting">
                <div className="field-name-draftsetting">
                  <div className="fieldname-back-draftsetting"></div>
                  {i + 1}炉運転焼却負荷率
                </div>
                <div className="field-values-draftsetting">
                  <div className="field-value-draftsetting">
                    <NumberInput
                      formValueState={formValueState}
                      className="input-draftsetting"
                      min={0}
                      max={100}
                      data-number-type="integer"
                      data-key={[JsonMap.burnLoad, i]}
                      value={formValues.burnLoad[i]}
                      disabled={!isEditablePermission}
                    />
                    <span className="unit-draftsetting">%</span>
                    <span>運転時処理量：</span>
                    <span>{Number(ceilNumber(operationalProcess[i], 2)).toLocaleString()}</span>
                    <span className="unit-draftsetting">t/日</span>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting"></div>
              ごみ比重
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="2"
                  min={0}
                  data-key={[JsonMap.trashDensity]}
                  value={formValues.trashDensity}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting">t/m3</span>
              </div>
            </div>
          </div>

          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting"></div>
              主灰原単位
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="5"
                  min={0}
                  max={1}
                  step={0.00001}
                  data-key={[JsonMap.ashRate]}
                  value={formValues.ashRate}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting"></span>
              </div>
            </div>
          </div>

          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting" style={{ height: '50px' }}></div>
              トラック1台当たり主灰
              <br />
              搬出量
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="2"
                  min={0}
                  max={29}
                  data-key={[JsonMap.truckMainAshCarryOut]}
                  value={formValues.truckMainAshCarryOut}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting">t/台</span>
              </div>
            </div>
          </div>

          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting"></div>
              飛灰原単位
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="5"
                  min={0}
                  max={1}
                  step={0.00001}
                  data-key={[JsonMap.flyAshRate]}
                  value={formValues.flyAshRate}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting"></span>
              </div>
            </div>
          </div>

          <div className="row-draftsetting">
            <div className="field-name-draftsetting">
              <div className="fieldname-back-draftsetting" style={{ height: '50px' }}></div>
              トラック1台当たり飛灰
              <br />
              搬出量
            </div>
            <div className="field-values-draftsetting">
              <div className="field-value-draftsetting">
                <NumberInput
                  formValueState={formValueState}
                  className="input-draftsetting"
                  data-significant-digits="2"
                  min={0}
                  max={29}
                  data-key={[JsonMap.truckFlyAshCarryOut]}
                  value={formValues.truckFlyAshCarryOut}
                  disabled={!isEditablePermission}
                />
                <span className="unit-draftsetting">t/台</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <br></br>
      <br></br>

      <div className="header-draftsetting"></div>
      <div className="item-title-draftsetting">ごみ搬入量設定</div>
      <div className="indent-draftsetting d-flex">
        <div className="row-draftsetting">
          <div className="field-name-draftsetting">
            <div className="fieldname-back-draftsetting" style={{ height: '27px' }}></div>
            <FormInput formValueState={formValueState} data-key={[JsonMap.settingMode]}>
              <input
                className="radioinput-draftsetting"
                type="radio"
                id="settingModeDirect"
                name="settingMode"
                value="file"
                checked={isFileMode}
                disabled={!isEditablePermission}
              />
            </FormInput>

            <label className="item-sub-title-draftsetting ml-1" htmlFor="settingModeDirect">
              ファイル設定
            </label>
          </div>

          <div className={` field-values-draftsetting ${isFileMode ? '' : 'v-hidden'}`}>
            <div className="field-value-draftsetting">
              <input type="file" id="importFile" style={{ display: 'none' }} onChange={applyByFile} accept=".csv" disabled={!isEditablePermission} />
              <Button disabled={!isEditablePermission} className="mx-2" variant="secondary" size="sm" onClick={inputByFile}>
                ファイル取り込み
              </Button>
              {formValues.settingCsvPath ? (
                <Button disabled={!isEditablePermission} className="mx-2" variant="secondary" size="sm" onClick={deleteInputByFile}>
                  取り込みデータクリア
                </Button>
              ) : (
                ''
              )}
              <span className="mx-2 mt-1">{formValues.settingCsvPath || ''}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="indent-draftsetting">
        <div className="row-draftsetting">
          <div className="field-name-draftsetting my-auto">
            <div className="fieldname-back-draftsetting" style={{ height: '27px' }}></div>
            <FormInput formValueState={formValueState} data-key={[JsonMap.settingMode]}>
              <input type="radio" id="settingModeCalculate" name="settingMode" value="calculation" checked={isCalculationMode} disabled={!isEditablePermission} />
            </FormInput>
            <label className="item-sub-title-draftsetting ml-1" htmlFor="settingModeCalculate">
              入力設定
            </label>
          </div>
        </div>
        {isCalculationMode && selectFiscalYear == nextFiscalYear ? (
          <div className="draft-carried-rate">
            <div className="flex-column draft-carried-rate-content">
              <div className="field-name-draftsetting carry-calculation-field-name py-1">
                <label>搬入割合[%]</label>
                <Button variant="outline-success" size="sm" className="ml-1" disabled={!isEditablePermission} onClick={resetCarryCalculation}>
                  リセット
                </Button>
                {/* 将来的に初期設定に戻す処理は必要だが、仕様が詰めれていないため、一時コメントアウト */}
                {/* <Button variant="outline-success" size="sm" className="ml-1" onClick={handleResetToOriginForCarryCalculationButtonClick}>
                  初期設定に戻す
                </Button> */}
              </div>
              <div className="row-draftsetting" style={{ paddingLeft: '0' }}>
                <div className="field-values-draftsetting d-block">
                  <div className="carry-calculation">
                    <div className="carry-calculation-values">
                      <span className="field-name-draftsetting carry-calculation-name">月別</span>
                      <div className="flex-row-draftsetting">
                        <div className="table-row-draftsetting">
                          <div className="cell-draftsetting">4月</div>
                          <div className="cell-draftsetting">5月</div>
                          <div className="cell-draftsetting">6月</div>
                          <div className="cell-draftsetting">7月</div>
                          <div className="cell-draftsetting">8月</div>
                          <div className="cell-draftsetting">9月</div>
                          <div className="cell-draftsetting">10月</div>
                          <div className="cell-draftsetting">11月</div>
                          <div className="cell-draftsetting">12月</div>
                          <div className="cell-draftsetting">1月</div>
                          <div className="cell-draftsetting">2月</div>
                          <div className="cell-draftsetting">3月</div>
                        </div>
                      </div>
                      <div className="flex-row-draftsetting">
                        <div className="table-row-draftsetting">
                          {new Array(12).fill(0).map((v, i) => (
                            <NumberInput
                              formValueState={formValueState}
                              key={`calcCell_${i}`}
                              className="cell-draftsetting"
                              data-significant-digits="2"
                              data-key={[JsonMap.monthlyCarryCalculation, i]}
                              value={formValues.monthlyCarryCalculation[i]}
                              min={0}
                              max={100}
                              step={0.5}
                              onBlur={updatePlanByYearlyTrash}
                              readOnly={selectFiscalYear === thisFiscalYear && selectMonthIndex - 3 - i > 0 ? true : false}
                              disabled={!isEditablePermission}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="draft-carry-calculation-sum">
                      <div className="text-center">合計</div>
                      <div className={`cell-draftsetting ma-auto ${hasErrorTotalMonthly && isInputAnyMonthly ? 'error' : ''}`} style={{ minWidth: '3em' }}>
                        {totalMonthly}
                      </div>
                    </div>
                  </div>
                  <div className="carry-calculation">
                    <div className="carry-calculation-values">
                      <span className="field-name-draftsetting carry-calculation-name">曜日別</span>
                      <div className="flex-row-draftsetting">
                        <div className="table-row-draftsetting">
                          <div className="cell-draftsetting">月</div>
                          <div className="cell-draftsetting">火</div>
                          <div className="cell-draftsetting">水</div>
                          <div className="cell-draftsetting">木</div>
                          <div className="cell-draftsetting">金</div>
                          <div className="cell-draftsetting">土</div>
                          <div className="cell-draftsetting">日</div>
                        </div>
                      </div>
                      <div className="flex-row-draftsetting">
                        <div className="table-row-draftsetting">
                          {new Array(7).fill(0).map((v, i) => (
                            <NumberInput
                              formValueState={formValueState}
                              key={`calcCell_${i}`}
                              className="cell-draftsetting"
                              data-significant-digits="2"
                              data-key={[JsonMap.weeklyCarryCalculation, i]}
                              value={formValues.weeklyCarryCalculation[i]}
                              min={0}
                              max={100}
                              step={0.5}
                              onBlur={updatePlanByYearlyTrash}
                              disabled={!isEditablePermission}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="draft-carry-calculation-sum">
                      <div className="text-center">合計</div>
                      <div className={`cell-draftsetting ma-auto ${hasErrorTotalWeekly && isInputAnyWeekly ? 'error' : ''}`} style={{ minWidth: '3em' }}>
                        {totalWeekly}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div></div>
        )}
        {isCalculationMode ? (
          <>
            <div className="row-draftsetting" style={{ display: 'flex' }}>
              <div className={`field-values-draftsetting my-auto ${isCalculationMode ? '' : 'd-none'}`}>
                <div className="field-name-draftsetting my-auto">
                  <div className="fieldname-back-draftsetting" style={{ height: '27px' }}></div>
                  年間計画ごみ搬入量
                </div>
                <div className="field-values-draftsetting">
                  <div className="field-value-draftsetting my-auto">
                    <NumberInput
                      formValueState={formValueState}
                      className="input-draftsetting"
                      min={Number(yearlyTrashCarryVolumeMin).toFixed()}
                      onBlur={updatePlanByYearlyTrash}
                      data-key={[JsonMap.yearlyTrashCarryVolume]}
                      value={Number(formValues.yearlyTrashCarryVolume).toFixed()}
                      readOnly={selectFiscalYear !== nextFiscalYear}
                      disabled={!isEditablePermission || selectFiscalYear !== nextFiscalYear}
                    />
                    <div className="my-auto unit-draftsetting">t</div>
                  </div>
                  <div className="field-value-draftsetting d-block">
                    <Button
                      variant="outline-success"
                      size="sm"
                      disabled={!isEditablePermission}
                      onClick={() =>
                        openNewTabComponent(
                          <AppPane>
                            <PastResult />
                          </AppPane>,
                          newWindowRef,
                        )
                      }
                    >
                      過去表示
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="row-draftsetting">
              <div className="cells-draftsetting">
                <div className="field-name-draftsetting">計画搬入量[t]</div>
                <div className="flex-row-draftsetting">
                  <div className="table-row-draftsetting">
                    <div className="cell-draftsetting">4月</div>
                    <div className="cell-draftsetting">5月</div>
                    <div className="cell-draftsetting">6月</div>
                    <div className="cell-draftsetting">7月</div>
                    <div className="cell-draftsetting">8月</div>
                    <div className="cell-draftsetting">9月</div>
                    <div className="cell-draftsetting">10月</div>
                    <div className="cell-draftsetting">11月</div>
                    <div className="cell-draftsetting">12月</div>
                    <div className="cell-draftsetting">1月</div>
                    <div className="cell-draftsetting">2月</div>
                    <div className="cell-draftsetting">3月</div>
                  </div>
                </div>
                {new Date(draftConditionStart) <= thisFiscalYearEnd && (
                  <div className="flex-row-draftsetting">
                    <div className="table-row-draftsetting">
                      {new Array(12).fill(0).map((v, i) => (
                        <div
                          className="cell-draftsetting"
                          style={{
                            background: getBackgroundColorByReferenceOfCarryVolume(i),
                          }}
                        >
                          {getTextByReferenceOfCarryVolume(i)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex-row-draftsetting">
                  <div className="table-row-draftsetting">
                    {new Array(12).fill(0).map((v, i) => (
                      <NumberInput
                        formValueState={formValueState}
                        key={`calcCell_${i}`}
                        className="cell-draftsetting"
                        min={Number(formValues.minCarryVolume[i]).toFixed()}
                        onBlur={updateYearlyTrashByPlan}
                        data-key={[JsonMap.planCarryVolume, i]}
                        // 表示上のみ整数 サーバに送信する値としては少数を許容
                        value={Number(formValues.planCarryVolume[i]).toFixed()}
                        readOnly={selectFiscalYear === thisFiscalYear && ((selectMonthIndex + 9) % 12) - i > 0 ? true : false}
                        disabled={!isEditablePermission || (selectFiscalYear === thisFiscalYear && ((selectMonthIndex + 9) % 12) - i > 0) ? true : false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="row-draftsetting">
              <div className="cells-draftsetting">
                <div className="field-name-draftsetting">{draftConditionFiscalYearStart}年度の計画搬入量[t]</div>
                <div className="flex-row-draftsetting">
                  <div className="table-row-draftsetting">
                    <div className="cell-draftsetting">4月</div>
                    <div className="cell-draftsetting">5月</div>
                    <div className="cell-draftsetting">6月</div>
                    <div className="cell-draftsetting">7月</div>
                    <div className="cell-draftsetting">8月</div>
                    <div className="cell-draftsetting">9月</div>
                    <div className="cell-draftsetting">10月</div>
                    <div className="cell-draftsetting">11月</div>
                    <div className="cell-draftsetting">12月</div>
                    <div className="cell-draftsetting">1月</div>
                    <div className="cell-draftsetting">2月</div>
                    <div className="cell-draftsetting">3月</div>
                  </div>
                </div>
                <div className="flex-row-draftsetting">
                  <div className="table-row-draftsetting">
                    {new Array(12).fill(0).map((v, i) => (
                      <span className="cell-draftsetting" style={{ overflow: 'auto', scrollbarWidth: 'none' }}>
                        {Number(formValues.prevPlanCarryVolume[i].toFixed()).toLocaleString()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div></div>
        )}
      </div>

      <br></br>
      <br></br>

      <div className="header-draftsetting"></div>
      <div className="item-title-draftsetting">停炉設定</div>
      <div className="indent-draftsetting pb-2">
        <div className="row-draftsetting">
          <div className="required-draftsetting field-name-draftsetting">
            <div className="fieldname-back-draftsetting"></div>
            共通停止期間
          </div>
          <div className="field-values-draftsetting">
            <div className="field-value-draftsetting">
              <div>
                <CustomDatePicker
                  className="dateinput-draftsetting"
                  minDate={new Date(formValues.draftConditionStart)}
                  maxDate={formValues.commonShutdownDatePlan[3] ? new Date(formValues.commonShutdownDatePlan[3]) : new Date(draftConditionEnd)}
                  selected={formValues.commonShutdownDatePlan[2] ? new Date(formValues.commonShutdownDatePlan[2]) : null}
                  onChange={(date: Date | null) => {
                    if (date != null) {
                      const newValues: [number, number, string, string, number, number] = [...formValues.commonShutdownDatePlan];
                      newValues[2] = toISODateStr(date);
                      setFormValues({
                        ...formValues,
                        commonShutdownDatePlan: newValues,
                      });
                    }
                  }}
                  dateFormat={`${DATE_FORMAT_YYYYMMDD_SLASH}(${
                    WEEK_DAYS[
                      formValues.commonShutdownDatePlan[2] ? new Date(formValues.commonShutdownDatePlan[2]).getDay() : new Date(new Date(formValues.draftConditionStart)).getDay()
                    ]
                  })`}
                  portalId="datepicker-portal"
                  locale={ja}
                  placeholderText="年/月/日(曜日)"
                  readOnly={formValues.commonShutdownDatePlan[4] ? true : false}
                  disabled={!isEditablePermission}
                />
              </div>
              <div className="mx-1">～</div>
              <div>
                <CustomDatePicker
                  className="dateinput-draftsetting"
                  minDate={formValues.commonShutdownDatePlan[2] ? new Date(formValues.commonShutdownDatePlan[2]) : new Date(formValues.draftConditionStart)}
                  maxDate={new Date(draftConditionEnd)}
                  selected={formValues.commonShutdownDatePlan[3] ? new Date(formValues.commonShutdownDatePlan[3]) : null}
                  onChange={(date: Date | null) => {
                    if (date != null) {
                      const newValues: [number, number, string, string, number, number] = [...formValues.commonShutdownDatePlan];
                      newValues[3] = toISODateStr(date);
                      setFormValues({
                        ...formValues,
                        commonShutdownDatePlan: newValues,
                      });
                    }
                  }}
                  dateFormat={`${DATE_FORMAT_YYYYMMDD_SLASH}(${
                    WEEK_DAYS[formValues.commonShutdownDatePlan[2] ? new Date(formValues.commonShutdownDatePlan[3]).getDay() : new Date(draftConditionEnd).getDay()]
                  })`}
                  portalId="datepicker-portal"
                  locale={ja}
                  placeholderText="年/月/日(曜日)"
                  readOnly={formValues.commonShutdownDatePlan[5] ? true : false}
                  disabled={!isEditablePermission}
                />
              </div>
            </div>
          </div>
        </div>
        {/* 将来的に実装予定だが、一時コメントアウト */}
        {/* <div className="row-draftsetting">
          <div>
            <div className="flex-row-draftsetting">
              <div className="table-row-draftsetting">
                <div className="cell-draftsetting"></div>
                <div className="cell-draftsetting">時刻[時]</div>
                <div className="cell-draftsetting">焼却時間</div>
                <div className="cell-draftsetting">発電時間</div>
              </div>
            </div>
            <div className="flex-row-draftsetting">
              {立下日、立上日の2日分}
              {new Array(2).fill(0).map((v, i) => (
                <div className="table-row-draftsetting">
                  <div className="cell-draftsetting">{i === 0 ? '立下日' : '立上日'}</div>
                  {時間}
                  <NumberInput
                    formValueState={formValueState}
                    className="cell-draftsetting"
                    min={0}
                    max={24}
                    data-key={[JsonMap.shutdownLaunchHour, i]}
                    value={formValues.shutdownLaunchHour[i]}
                  />
                  {焼却量}
                  <NumberInput
                    formValueState={formValueState}
                    className="cell-draftsetting"
                    min={0}
                    max={24}
                    data-key={[JsonMap.shutdownBurnHours, i]}
                    value={formValues.shutdownBurnHours[i]}
                  />
                  {発電量}
                  <NumberInput
                    formValueState={formValueState}
                    className="cell-draftsetting"
                    min={0}
                    max={24}
                    data-key={[JsonMap.shutdownElectricCreateHours, i]}
                    value={formValues.shutdownElectricCreateHours[i]}
                  />
                </div>
              ))}
            </div>
          </div>
        </div> */}

        {/* 個別炉停止期間は一時非表示 */}
        {/* <div className="row-draftsetting d-block">
          <div className="field-name-draftsetting">
            <div className="fieldname-back-draftsetting"></div>
            個別炉停止期間
          </div>
          <div className="field-values-draftsetting flex-column" style={{ alignItems: 'baseline' }}>
            {(formValues.shutdownDatePlans || []).map((_v, i) => (
              <div key={formValues.shutdownDatePlans[i][0]} className="mt-2">
                <div className="field-value-draftsetting">
                  <div key={`shutdownDatePlansRow_${i}`} className="field-value-draftsetting">
                    <FormInput formValueState={formValueState} data-key={[JsonMap.shutdownDatePlans, i, 1]}>
                      <select className="d-select" value={formValues.shutdownDatePlans[i][1]}>
                        {new Array(getResponse.furnacesCount).fill(0).map((_v, i) => (
                          <option>{i + 1}</option>
                        ))}
                      </select>
                    </FormInput>
                    <div className="unit-draftsetting">号炉</div>
                  </div>
                  <div className="field-value-draftsetting">
                    <div>
                      <FormInput formValueState={formValueState} data-key={[JsonMap.shutdownDatePlans, i, 2]}>
                      <input
                        className="dateinput-draftsetting"
                        type="date"
                        min={draftConditionStart}
                        max={toISODateStr(formValues.shutdownDatePlans[i][3]) || draftConditionEnd}
                        value={formValues.shutdownDatePlans[i][2]}
                      />
                      </FormInput>
                      <img className="calendericon-draftsetting" src={calecderIcon} height={'16px'}></img>
                    </div>
                    <div className="mx-1">～</div>
                    <div>
                      <FormInput formValueState={formValueState} data-key={[JsonMap.shutdownDatePlans, i, 3]}>
                      <input
                        className="dateinput-draftsetting"
                        type="date"
                        min={toISODateStr(formValues.shutdownDatePlans[i][2]) || draftConditionStart}
                        max={draftConditionEnd}
                        value={formValues.shutdownDatePlans[i][3]}
                      />
                      </FormInput>
                      <img className="calendericon-draftsetting" src={calecderIcon} height={'16px'}></img>
                    </div>
                    <div className="field-value-draftsetting">
                    <FormInput formValueState={formValueState} data-key={[JsonMap.shutdownDatePlans, i, 4]}>
                      <PrioritySelector value={formValues.shutdownDatePlans[i][4]}/>
                    </FormInput>
                    </div>
                    <div>
                      <Button
                        disabled={disabledButtons}
                        variant="outline-secondary"
                        size="sm"
                        className="button-draftsetting ml-1"
                        onClick={(e) => deleteShutdownDatePlanField(e, i)}
                      >
                        削除
                      </Button>
                    </div>
                  </div>
                  <div>
                    {i === formValues.shutdownDatePlans.length - 1 ? (
                      <Button disabled={disabledButtons} variant="outline-secondary" size="sm" className="addbutton-draftsetting" onClick={addShutdownDatePlanField}>
                        追加
                      </Button>
                    ) : (
                      <div></div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <br></br> */}

        <div className="row-draftsetting">
          {(formValues.shutdowns || []).map((_v, i) => (
            <div key={`shutdowns_${i}`} className="d-flex">
              <div className="required-draftsetting field-name-draftsetting">
                <div className="fieldname-back-draftsetting" style={{ height: '75px' }}></div>
                {draftConditionStart}以降の
                <br />
                {i + 1}号炉停炉回数
                <br></br>
                <div className="field-value-draftsetting font-mini">※共通停止を含む</div>
              </div>
              <div className="field-values-draftsetting" style={{ marginLeft: '40px' }}>
                <div className="field-value-draftsetting">
                  <NumberInput
                    formValueState={formValueState}
                    className="input-draftsetting"
                    data-number-type="integer"
                    min={0}
                    max={10}
                    data-key={[JsonMap.shutdowns, i]}
                    value={formValues.shutdowns[i]}
                    disabled={!isEditablePermission}
                  />
                  <div className="unit-draftsetting">回</div>
                </div>
                <div className="field-value-draftsetting" style={{ marginRight: '20px' }}>
                  <div>
                    <Button
                      variant="outline-success"
                      size="sm"
                      disabled={!isEditablePermission}
                      onClick={() =>
                        openNewTabComponent(
                          <AppPane>
                            <PastResult />
                          </AppPane>,
                          newWindowRef,
                        )
                      }
                    >
                      過去表示
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <br></br>
      <div className="postrow-draftsetting py-1" style={{ flexDirection: 'row-reverse' }}>
        <Button disabled={!isEditablePermission} variant="success" className="post-draftsetting" onClick={() => handleRegister(true)}>
          立案開始
        </Button>
        <Button disabled={!isEditablePermission} variant="success" className="post-draftsetting" onClick={() => handleRegister(false)}>
          一時保存
        </Button>
      </div>
      <br />
    </div>
  );
};

export default Page;
